import { clamp } from 'lodash-es';
import {
  type RefCallback,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  _getFirstFocusableChild,
  _getLastFocusableChild,
} from '@dynatrace/strato-components/core';

import { usePrevious } from './usePrevious.js';

// #region Typings

/**
 * Option type for the `useRovingFocus` hook, allowing to configure certain behaviors.
 * @public
 */
export type RovingFocusOptions = {
  /**
   * Index of the item that should initially be set to tabIndex = 0.
   * @defaultValue 0
   */
  initialIndex?: number;

  /**
   * The arrangement of the navigable items. Determines which arrow keys are used for navigation.
   * @defaultValue vertical
   */
  direction?: 'horizontal' | 'vertical' | 'both';

  /**
   * Determines whether the elements should be loopable.
   * @defaultValue true
   */
  loopFocus?: boolean;

  /**
   * The selector used to retrieve all the navigable children.
   * @defaultValue *
   */
  focusableChildrenSelector?: string;
};

/**
 * @public
 */
export type RovingFocusReturnType<T, V> = {
  /**
   * Reference of the container using roving focus.
   * @defaultValue RefCallback<T>
   */
  containerRef: RefCallback<T>;

  /**
   * Currently active element.
   * @defaultValue undefined
   */
  activeElement: V | undefined;
};
// #endregion Typings

// TODO: Do we want to support nested roving containers?
// Could also work with adding additional options for grouping children.
// see: ADES-369

// TODO: Use TreeWalker instead of just selecting all children matching given selector
// see: ADES-607

/**
 * Check if the element is focusable and focus it if it is.
 * @param element - DOM node to focus.
 * @returns true if the element was focused and false if it's not focusable
 */
const tryFocusElement = (element: HTMLElement) => {
  // if focus is already where we want to focus, we don't want to keep going
  if (element === document.activeElement) {
    return true;
  }

  try {
    element.focus();
  } catch {
    // continue regardless of error
  }

  return document.activeElement === element;
};

/**
 * Set focus on the first focusable element of the roving focus children or the child at the given index.
 * @param rovingFocusChildren - DOM node for which to find the first focusable descendant.
 * @returns The index of the element that was focused or undefined if nothing was focused
 */
const focusFirstOrAtIndex = (
  rovingFocusChildren: HTMLElement[] | null,
  index?: number,
) => {
  if (!rovingFocusChildren) {
    return;
  }

  const previouslyFocusedEl = document.activeElement;
  const elIndex =
    index !== undefined ? clamp(index, 0, rovingFocusChildren.length - 1) : 0;

  const element = rovingFocusChildren[elIndex];

  // if focus is already where we want to focus, we don't want to keep going
  if (tryFocusElement(element)) {
    return elIndex;
  } else {
    element?.setAttribute('tabindex', '-1'); // set tabindex to -1, because it's not focusable and might have had tabindex 0
  }

  for (const [key, el] of rovingFocusChildren.entries()) {
    if (key === elIndex) {
      continue; // we already checked the initial index
    }

    // if focus is already where we want to focus, we don't want to keep going
    if (el === previouslyFocusedEl) {
      return;
    }

    try {
      el.focus();
    } catch {
      // continue regardless of error
    }

    if (document.activeElement !== previouslyFocusedEl) {
      return key;
    }

    el.setAttribute('tabindex', '-1'); // set tabindex to -1, because it's not focusable and might have had tabindex 0
  }
};

/**
 * Given an array of elements and the key code, determine which is the next element that can be focused and focus it if it's focusable.
 * @returns the index of the focused element
 */
const tryFocusElementAtNextIndex = (
  code: string,
  elementIndex: number,
  elements: HTMLElement[],
  loopFocus: boolean,
  initialElementIndex?: number,
): number | null => {
  // if we're trying to focus an element at index -1, it means the element can't be found in the array of focusable elements (e.g. all children inside the container were hidden)
  if (elementIndex === -1) {
    return null;
  }

  if (initialElementIndex === elementIndex) {
    return null; // then we already looped through all elements and if we are back at the initialElementIndex, there was nothing focusable, so we need to abort to avoid running into an endless loop
  }

  let nextIndex = -1;
  const lastElementIndex = elements.length - 1;

  if (code === 'ArrowRight' || code === 'ArrowDown') {
    // Move focus to next focusable element.
    // If not loopable, clamp to the last element index.
    nextIndex = loopFocus
      ? (elementIndex + 1) % elements.length
      : clamp(elementIndex + 1, lastElementIndex);
  } else if (code === 'ArrowLeft' || code === 'ArrowUp') {
    // Move focus to previous focusable element.
    // If not loopable, clamp to zero.
    const newIndex = elementIndex - 1;
    if (loopFocus) {
      nextIndex = newIndex < 0 ? lastElementIndex : newIndex;
    } else {
      nextIndex = Math.max(newIndex, 0);
    }
  }

  const element = elements[nextIndex];

  // Focus the new focusable element if it's focusable
  // This triggers the event listener updating the active element, which
  // in turn triggers the effect for setting the tab index.
  if (tryFocusElement(element)) {
    return nextIndex;
  } else {
    // when we navigate the items via keyboard or to the container, we need to set the tabIndex to -1 if an item is not focusable anymore (due to conditional rendering)
    // it also might have had tabindex 0 before conditional rendering
    element?.setAttribute('tabindex', '-1');
    // try to focus the next element if the previous one wasn't focusable
    return tryFocusElementAtNextIndex(
      code,
      nextIndex,
      elements,
      loopFocus,
      initialElementIndex ?? elementIndex,
    );
  }
};

/**
 * The `useRovingFocus` hook applies a roving tab index to the children matching
 * the given selector and enables you to navigate the elements using the arrow
 * keys. Applying a roving tab index to a list of items enables you to efficiently
 * navigate through the app, having a single tab-stop for a collection of items.
 * Navigating back to the collection restores the focus to the item that has been
 * focused when navigating away, enabling you to return to where you left off.
 * @public
 */
export function useRovingFocus<
  T extends HTMLElement,
  V extends HTMLElement = HTMLElement,
>({
  initialIndex = 0,
  direction = 'vertical',
  loopFocus = true,
  focusableChildrenSelector = ':scope > *',
}: RovingFocusOptions = {}): RovingFocusReturnType<T, V> {
  const [activeElement, setActiveElement] = useState<V | undefined>();
  const previousActiveElement = usePrevious<V | undefined>(activeElement);
  const containerRef = useRef<T | null>(null);
  const isTabbingBackOutRef = useRef(false);
  const initialIndexRef = useRef(initialIndex); // ref used to store the index when navigating via keyboard and reset it when more/fewer elements become focusable (e.g. on resize)
  const isClickFocusRef = useRef(false); // ref used for fixing issues when an item is clicked and there's an attached focus handler that can collide with our roving focus handling (e.g. clicking on a radio button focuses/selects the first element and then the clicked one - ADES-3361)

  const [childNodesCount, setChildNodesCount] = useState(0);

  useEffect(() => {
    const observer = new MutationObserver((mutationsList) => {
      for (const mutation of mutationsList) {
        if (mutation.type === 'childList') {
          setChildNodesCount(containerRef.current?.childNodes.length ?? 0);
        }
      }
    });

    if (containerRef.current) {
      observer.observe(containerRef.current, {
        childList: true,
        attributes: false,
      });
    }

    return () => {
      observer.disconnect();
    };
  }, []);

  const focusableElementsRefs = useRef<V[]>([]);

  const resetActiveElement = useCallback(() => {
    initialIndexRef.current = initialIndex;
    setActiveElement(
      focusableElementsRefs.current[
        clamp(initialIndex, 0, focusableElementsRefs.current.length - 1)
      ],
    );
  }, [initialIndex]);

  // Set the currently active element to the event target when it receives focus.
  const handleElementFocus = ({ target }: FocusEvent) => {
    if (target !== null && target instanceof HTMLElement) {
      setActiveElement(target as V);
    }
  };

  // Focus clicked element to update currently active element.
  const handleElementClick = ({ currentTarget }: MouseEvent) => {
    if (currentTarget !== null && currentTarget instanceof HTMLElement) {
      currentTarget.focus();
      initialIndexRef.current = focusableElementsRefs.current.indexOf(
        currentTarget as V,
      );
    }
  };

  const handleFocus = ({ target }: FocusEvent) => {
    if (isTabbingBackOutRef.current) {
      if (target !== null && target instanceof HTMLElement) {
        handleShiftTab(target);
      }
      return;
    }

    if (isClickFocusRef.current) {
      return;
    }

    const newIndex = focusFirstOrAtIndex(
      focusableElementsRefs.current,
      initialIndexRef.current,
    );

    if (newIndex !== undefined) {
      initialIndexRef.current = newIndex;
    }
  };

  /**
   * We need to handle shift tabbing manually to move the focus to the previously focusable element (and the container has tabindex 0 in order to make it focusable even if a previously focused child has been hidden).
   * Otherwise, the focus would be on the wrapping container and the user would need to shift tab again to get there.
   */
  const handleShiftTab = (target: HTMLElement) => {
    focusPreviousFocusableNode(target);
    isTabbingBackOutRef.current = false; // reset the tabbing back out state
  };

  /**
   * Find the first focusable node closest to the roving focus container and set the focus to that node. If no focusable node can be found, reset the focus to the body.
   * So when we shift-tab, we just move the focus to the previously focused element and we don't change any tabindex attribute.
   */
  const focusPreviousFocusableNode = (
    target?: HTMLElement | Node | null,
  ): HTMLElement | null => {
    if (!target) {
      return null;
    }

    const prevSibling = target.previousSibling as HTMLElement;

    if (prevSibling !== null) {
      // if the previous sibling has one child only or none, we don't need a tree walker and can just try to focus the element and see if it's focusable => then we could have an early exit
      if (prevSibling.children?.length <= 1 && tryFocusElement(prevSibling)) {
        return prevSibling;
      }

      // if there are more children, we need to check the last focusable child with the tree walker to shift-tab to it
      /* e.g. (second button should be focused)
      <div>
        <Button onClick={() => setShowItem1(!showItem1)}>Toggle button 1</Button>
        <Button onClick={() => setShowItem2(!showItem2)}>Toggle button 2</Button>
        <div>I'm not focusable</div>
      </div>
      <RadioGroup >
      {...}
      </RadioGroup>
      */
      const lastFocusableChild = _getLastFocusableChild(
        prevSibling,
      ) as HTMLElement;

      if (lastFocusableChild && tryFocusElement(lastFocusableChild)) {
        return lastFocusableChild; // if it's focusable, we can return it
      }

      if (prevSibling.previousSibling !== null) {
        // if there's still a previous sibling in the container, we need to check from there if we can focus any element inside
        return focusPreviousFocusableNode(prevSibling);
      }

      // if there is no previous sibling, there is no focusable element left to check in the container, so we need to go up one level in the tree and check from there again
      return focusPreviousFocusableNode(prevSibling.parentNode);
    }

    // if there's no previous sibling, we check the parent node
    const prevFocusableEl = target.parentNode
      ? _getFirstFocusableChild(target.parentNode)
      : null;

    // no previous sibling and the parent node has a focusable child
    if (prevFocusableEl) {
      // if the first focusable element of the parent node is still the roving focus container, we need to check the parent node for previous focusable nodes
      /* e.g.: The container-parent div has the roving focus container as first focusable element, so we need to check recursively from the container-parent div (if there is a previous sibling - button - it gets the focus, or continue to the parent div if there was no focusable element).
      Otherwise, we would focus the roving focus container, which is only focusable to be able to handle shift-tabbing properly but should not actually receive focus.

      <div id="parent">
        <Button onClick={() => setShowItem1(!showItem1)}>Toggle button 1</Button>
        <div id="container-parent">
          <RadioGroup>
          {...}
          </RadioGroup>
        </div>
      </div>
      */
      return focusPreviousFocusableNode(target.parentNode);
    }

    if (
      document.activeElement &&
      document.activeElement instanceof HTMLElement
    ) {
      document.activeElement.blur();
    }

    document.body.focus(); // if we reach this, it means there is no focusable element before the container in the DOM, so we need to focus the body
    /* e.g.
      <body>
        <div>
          <RadioGroup>
          {...}
          </RadioGroup>
        </div>
      </body>
    */
    return null;
  };

  const handleBlur = () => {
    isTabbingBackOutRef.current = false;
    isClickFocusRef.current = false;
  };

  const handleMouseDown = () => (isClickFocusRef.current = true);

  // Moves focus according to the direction and pressed key.
  const handleKeydown = useCallback(
    (event: KeyboardEvent) => {
      const { key, target } = event;

      // Only change indices when navigating with arrow keys corresponding to the roving focus direction.
      const isValidKeystroke =
        ((direction === 'horizontal' || direction === 'both') &&
          ['ArrowLeft', 'ArrowRight'].includes(key)) ||
        ((direction === 'vertical' || direction === 'both') &&
          ['ArrowUp', 'ArrowDown'].includes(key));

      if (isValidKeystroke) {
        event.preventDefault();

        // Get index of event target.
        const elementIndex = focusableElementsRefs.current.indexOf(target as V);

        const nextIndex = tryFocusElementAtNextIndex(
          key,
          elementIndex,
          focusableElementsRefs.current,
          loopFocus,
        );

        if (nextIndex !== null) {
          initialIndexRef.current = nextIndex;
        }
      } else if (key === 'Tab' && event.shiftKey) {
        isTabbingBackOutRef.current = true;
      }
    },
    [direction, loopFocus],
  );

  // Use a callback ref for the roving focus hook container to be able to react
  // to updates changing references and conditionally rendered containers.
  const setRef = useCallback(
    (node: T) => {
      const container = containerRef.current;
      // Clean up event listeners of previous container
      if (container) {
        container.removeEventListener('keydown', handleKeydown);
        container.removeEventListener('focus', handleFocus);
        container.removeEventListener('blur', handleBlur);
        container.removeEventListener('mouseDown', handleMouseDown);

        for (const element of focusableElementsRefs.current) {
          element.removeEventListener('focus', handleElementFocus);
          element.removeEventListener('click', handleElementClick);
        }
      }

      // Initialize event listeners for roving focus container and children.
      if (node) {
        focusableElementsRefs.current = Array.from(
          node.querySelectorAll<V>(focusableChildrenSelector),
        );

        node.addEventListener('keydown', handleKeydown);

        for (const [
          index,
          element,
        ] of focusableElementsRefs.current.entries()) {
          // Set all tab indices to -1 if the active element is not yet set (initial render).
          // Else, when changing props, the active element's tab index would also be set to -1
          // and nothing would be focusable anymore.

          (!activeElement || activeElement !== element) &&
            element.setAttribute(
              'tabindex',
              index === initialIndexRef.current ? '0' : '-1',
            );
          // reset the initial index when the container is reset (e.g. when resizing causes for example the AppHeader home link to switch to/from the
          // burger menu, which would offset the initialIndex used when navigating via keyboard)
          initialIndexRef.current = initialIndex;
          element.addEventListener('click', handleElementClick);
          element.addEventListener('focus', handleElementFocus);
        }
      }

      containerRef.current = node;

      if (containerRef.current) {
        containerRef.current.style.outline = 'none';
        containerRef.current.setAttribute('tabIndex', '0');
        containerRef.current.addEventListener('focus', handleFocus);
        containerRef.current.addEventListener('blur', handleBlur);
        containerRef.current.addEventListener('mousedown', handleMouseDown);
        isClickFocusRef.current = false;
      }
    },
    // Excluding initialIndex and activeElement.
    // A change in neither of them should lead to this effect running again, since
    // it would clean-up and re-add the event listeners every time.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [focusableChildrenSelector, handleKeydown, childNodesCount],
  );

  // Update tab indices of previous and active element.
  useEffect(() => {
    if (activeElement !== previousActiveElement) {
      previousActiveElement?.setAttribute('tabindex', '-1');
      activeElement?.setAttribute('tabindex', '0');
    }
  }, [activeElement, previousActiveElement]);

  // Set the currently active element and update the initialIndexRef if the initial index or focusableChildrenSelector changes.
  useEffect(resetActiveElement, [
    focusableChildrenSelector,
    resetActiveElement,
  ]);

  return { containerRef: setRef, activeElement };
}
